import React, { useEffect, useState, useRef } from "react";
import { IconAlertCircle } from "@tabler/icons-react";
import {
    Table,
    Button,
    Input,
    Space,
    message,
    Popconfirm,
    Modal,
    Select,
    Tag,
    Typography,
    Descriptions,
    Dropdown,
    Col,
} from "antd";
import {
    SearchOutlined,
    ReloadOutlined,
    EyeOutlined,
    AppstoreOutlined,
    UndoOutlined,
    FilterOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { usePreference } from "../../context/PreferenceContext";
import OrdersServ from "../../service/OrdersServ";
import ShopServ from "../../service/ShopServ";
import CategoryServ from "../../service/CategoryServ";
import SubCategoryServ from "../../service/SubCategoryServ";
import Breadcrumbs from "../../component/Breadcrumbs";
import dayjs from "dayjs";
import TableImage from "../../component/ui/TableImage";
import { getImageUrl } from "../../component/SafeImage";
import { formatDate, formatDateTime } from "../../util/dateUtils";
import { formatUnit } from "../../util/unitFormatter";
import { getOrderRowClass, getEffectiveStatus } from "../../util/statusHighlight";
import EmptyState from "../../util/EmptyState";
import OrderStatusDrawer from "../../component/OrderStatusDrawer";
import { FilterBar } from "../../component/ui/FilterBar";
import SearchInput from "../../component/ui/SearchInput";
import { ResetButton } from "../../component/ui/ActionButtons";
import DataTable from "../../component/ui/DataTable";
import io from "socket.io-client";

const { Option } = Select;
const { Text } = Typography;

const DeliveryCountdown = ({ createdAt, minutes, label, isActive = true }) => {
    const [timeLeft, setTimeLeft] = useState(0);

    useEffect(() => {
        if (!isActive) return;
        const update = () => {
            const diff = dayjs(createdAt).add(minutes, 'minute').diff(dayjs(), 'second');
            setTimeLeft(diff > 0 ? diff : 0);
        };
        update();
        const interval = setInterval(update, 1000);
        return () => clearInterval(interval);
    }, [createdAt, minutes, isActive]);

    if (!isActive) return <span>{label}</span>;
    if (timeLeft <= 0) return <span>{label} (Arriving Soon)</span>;
    
    const h = Math.floor(timeLeft / 3600);
    const m = Math.floor((timeLeft % 3600) / 60);
    const s = timeLeft % 60;
    
    const timeStr = [
        h > 0 ? h.toString().padStart(2, '0') : null,
        m.toString().padStart(2, '0'),
        s.toString().padStart(2, '0')
    ].filter(Boolean).join(':');
    
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
            <span>{label}</span>
            <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 6, 
                background: '#fff7ed', 
                padding: '4px 8px', 
                borderRadius: 6, 
                border: '1px solid #ffedd5', 
                width: 'fit-content' 
            }}>
                <span style={{ fontSize: 12 }}>⏱️</span>
                <span style={{ fontSize: 12, fontWeight: 800, color: '#c2410c', fontFamily: 'monospace', letterSpacing: '0.5px' }}>
                    {timeStr}
                </span>
            </div>
        </div>
    );
};

const OrderIssues = () => {
    const navigate = useNavigate();
    const { formatPrice } = usePreference();
    const location = useLocation();
    const socketRef = useRef(null);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [totalOrders, setTotalOrders] = useState(0);
    const [viewModalVisible, setViewModalVisible] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [updatingStatus, setUpdatingStatus] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const [requestModalVisible, setRequestModalVisible] = useState(false);
    const [requestDate, setRequestDate] = useState(null);
    const [requestTime, setRequestTime] = useState(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    // Filter States
    const [allShops, setAllShops] = useState([]);
    const [allCategories, setAllCategories] = useState([]);
    const [allSubCategories, setAllSubCategories] = useState([]);
    const [filteredSubCategories, setFilteredSubCategories] = useState([]);
    const [selectedShop, setSelectedShop] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedSubCategory, setSelectedSubCategory] = useState(null);
    const [selectedPaymentStatus, setSelectedPaymentStatus] = useState(null);
    const [selectedOrderStatus, setSelectedOrderStatus] = useState(null);

    // New Modals State
    const [rescheduleModalVisible, setRescheduleModalVisible] = useState(false);
    const [rescheduleReason, setRescheduleReason] = useState("");

    const [requestUpdateModalVisible, setRequestUpdateModalVisible] = useState(false);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [issueActionType, setIssueActionType] = useState(null);
    const [cancellationReason, setCancellationReason] = useState("");
    const [availabilityOption, setAvailabilityOption] = useState(null);
    const [historyModalVisible, setHistoryModalVisible] = useState(false);

    useEffect(() => {
        if (location.state?.highlightOrderId && orders.length > 0) {
            const order = orders.find(o => o._id === location.state.highlightOrderId);
            if (order) {
                setSelectedOrder(order);
                setIsDrawerOpen(true);
            }
        }
    }, [location.state, orders]);

    // 🧠 Fetch Seller Orders
    // Accepts explicit overrides to avoid stale closure issues (e.g. on Reset)
    const fetchOrders = async (searchingText = "", overrides = {}) => {
        try {
            setLoading(true);
            const params = {};
            if (searchingText) params.search = searchingText;

            const shop = 'shopId' in overrides ? overrides.shopId : selectedShop;
            const category = 'categoryId' in overrides ? overrides.categoryId : selectedCategory;
            const subCategory = 'subCategoryId' in overrides ? overrides.subCategoryId : selectedSubCategory;
            const paymentStatus = 'paymentStatus' in overrides ? overrides.paymentStatus : selectedPaymentStatus;
            const orderStatus = 'status' in overrides ? overrides.status : selectedOrderStatus;

            const filter = {};
            if (shop) filter.shopId = shop;
            if (category) filter.categoryId = category;
            if (subCategory) filter.subCategoryId = subCategory;
            if (paymentStatus) filter.paymentStatus = paymentStatus;
            if (orderStatus) filter.status = orderStatus;

            if (Object.keys(filter).length > 0) {
                params.filter = JSON.stringify(filter);
            }

            const res = await OrdersServ.getSellerAllOrders(params);
            const data = res?.data || [];
            const activeOrders = data.filter(o => {
                const hasIssues = o.productIssueHistory && o.productIssueHistory.length > 0;
                return hasIssues;
            });
            setOrders(activeOrders);
            setTotalOrders(activeOrders.length);
            setSelectedOrder(prev => {
                if (!prev) return prev;
                const found = activeOrders.find(o => o._id === prev._id);
                return found || prev;
            });
        } catch (err) {
            console.error("Error fetching orders:", err);
            message.error("Failed to fetch orders");
        } finally {
            setLoading(false);
        }
    };

    // ✅ Fetch filters on mount
    useEffect(() => {
        const fetchFilters = async () => {
            try {
                const [shopsRes, catsRes, subsRes] = await Promise.all([
                    ShopServ.getSellerShops(),
                    CategoryServ.index({ limit: 1000, filter: JSON.stringify({ parentId: null }) }),
                    SubCategoryServ.index({ limit: 2000 })
                ]);

                setAllShops(Array.isArray(shopsRes) ? shopsRes : shopsRes?.data || []);
                setAllCategories(Array.isArray(catsRes) ? catsRes : catsRes?.data || []);
                setAllSubCategories(Array.isArray(subsRes) ? subsRes : subsRes?.data || []);
            } catch (error) {
                console.error("Error fetching filter data:", error);
            }
        };
        fetchFilters();
    }, []);

    // ✅ Filter subcategories when category changes
    useEffect(() => {
        if (!selectedCategory) {
            setFilteredSubCategories([]);
            setSelectedSubCategory(null);
            return;
        }
        const subs = allSubCategories.filter(s => (s.parentId?._id || s.parentId) === selectedCategory || (s.categoryId?._id || s.categoryId) === selectedCategory);
        setFilteredSubCategories(subs);
        setSelectedSubCategory(null);
    }, [selectedCategory, allSubCategories]);

    useEffect(() => {
        const handler = setTimeout(() => {
            fetchOrders(searchText);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchText, selectedShop, selectedCategory, selectedSubCategory, selectedPaymentStatus, selectedOrderStatus]);

    useEffect(() => {
        const token = localStorage.getItem("token") || localStorage.getItem("sellerToken");
        let socket;

        if (token) {
            try {
                const socketUrl = process.env.REACT_APP_API_BASE_URL ? process.env.REACT_APP_API_BASE_URL.replace(/\/api\/?$/, '') : 'http://localhost:4000';
                socket = io(socketUrl);

                // Extract user ID from token
                const base64Url = token.split('.')[1];
                const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                const payload = JSON.parse(window.atob(base64));
                const userId = payload.id || payload._id;

                if (userId) {
                    socket.emit('join', userId);
                    socketRef.current = socket;
                    
                    const handleUpdate = (data) => {
                        console.log("🚀 Seller Socket - Order Status Updated:", data);
                        fetchOrders();
                        if (data && data.status) {
                            const statusLabel = data.status.replace(/_/g, ' ').toUpperCase();
                            message.info(`Order #${data.orderId || data.order?.orderId} is now ${statusLabel}`);
                        }
                        if (data && data.order && data.orderId) {
                            setSelectedOrder(prev => (prev && prev._id === data.orderId) ? data.order : prev);
                        }
                    };

                    socket.on('orderStatusUpdated', handleUpdate);
                    socket.on('SELLER_ORDER_UPDATE', handleUpdate);
                }
            } catch (e) {
                console.error("Socket connection failed in Seller Orders", e);
            }
        }

        return () => {
            if (socket) socket.disconnect();
        };
    }, []);

    useEffect(() => {
        if (socketRef.current && allShops && allShops.length > 0) {
            allShops.forEach(shop => {
                const shopId = shop._id || shop;
                if (shopId) {
                    socketRef.current.emit('join', shopId);
                }
            });
        }
    }, [allShops]);

    const handleView = (record) => {
        setSelectedOrder(record);
        setIsDrawerOpen(true);
    };

    // 🚚 Update Order Status
    const handleStatusUpdate = async (id, newStatus) => {
        try {
            setUpdatingStatus(true);
            const res = await OrdersServ.updateStatus(id, newStatus, `Status updated to ${newStatus} by Seller`);
            message.success(`Order status updated to ${newStatus}`);
            fetchOrders();
            if (selectedOrder && selectedOrder._id === id) {
                const updatedOrder = res?.data || res;
                if (updatedOrder && updatedOrder.statusHistory) {
                    setSelectedOrder(updatedOrder);
                } else {
                    const newHistoryEntry = { status: newStatus, timestamp: new Date().toISOString() };
                    const updatedHistory = selectedOrder.statusHistory ? [...selectedOrder.statusHistory, newHistoryEntry] : [newHistoryEntry];
                    setSelectedOrder({ ...selectedOrder, status: newStatus, statusHistory: updatedHistory });
                }
            }
        } catch (error) {
            console.error("Error updating status:", error);
            message.error("Failed to update status");
        } finally {
            setUpdatingStatus(false);
        }
    };

    const handleRequestTimeChange = async () => {
        if (!selectedOrder || !requestDate || !requestTime) {
            message.warning("Please select both date and time slot");
            return;
        }
        try {
            setUpdatingStatus(true);
            await OrdersServ.requestDeliveryTimeChange(selectedOrder._id, {
                requestedDate: requestDate.toISOString(),
                requestedTime: requestTime
            });
            message.success("Delivery time change requested successfully");
            setRequestModalVisible(false);
            setViewModalVisible(false);
            fetchOrders();
        } catch (error) {
            message.error(error.message || "Failed to request time change");
        } finally {
            setUpdatingStatus(false);
        }
    };

    // 🗑 Delete Order
    const handleDelete = async (id) => {
        try {
            await OrdersServ.remove(id);
            message.success("Order removed successfully");
            fetchOrders();
        } catch (error) {
            console.error("Error deleting order:", error);
            message.error("Failed to remove order");
        }
    };

    // --- NEW SELLER ACTIONS ---
    const handleSellerReschedule = async () => {
        if (!selectedOrder || !requestDate || !requestTime) {
            message.warning("Date and time are required");
            return;
        }
        try {
            setUpdatingStatus(true);
            await OrdersServ.sellerReschedule(selectedOrder._id, {
                newDate: requestDate.toISOString(),
                newTimeSlot: requestTime,
                reason: rescheduleReason
            });
            message.success("Order rescheduled successfully");
            setRescheduleModalVisible(false);
            fetchOrders();
        } catch (err) {
            message.error(err.message || "Failed to reschedule order");
        } finally {
            setUpdatingStatus(false);
        }
    };

    const handleSellerRequestUpdate = async () => {
        if (!issueActionType) {
            message.warning("Please select an issue action type");
            return;
        }
        if (issueActionType !== 'CANCEL_FULL_ORDER' && selectedProducts.length === 0) {
            message.warning("Select at least one product");
            return;
        }
        if (issueActionType === 'OUT_OF_STOCK' && !availabilityOption) {
            message.warning("Please select product availability status");
            return;
        }
        if ((issueActionType === 'CANCEL_SELECTED_PRODUCT' || issueActionType === 'CANCEL_FULL_ORDER') && !cancellationReason.trim()) {
            message.warning("Please enter a cancellation reason");
            return;
        }
        try {
            setUpdatingStatus(true);
            await OrdersServ.sellerRequestUpdate(selectedOrder._id, {
                issueType: issueActionType,
                selectedProducts: issueActionType === 'CANCEL_FULL_ORDER' ? selectedOrder.items.map(i => i.product?._id || i.product) : selectedProducts,
                cancellationReason: cancellationReason,
                availabilityReason: issueActionType === 'OUT_OF_STOCK' ? availabilityOption : undefined,
            });
            message.success("Order issue request sent to buyer");
            setRequestUpdateModalVisible(false);
            setSelectedProducts([]);
            setIssueActionType(null);
            setCancellationReason("");
            setAvailabilityOption(null);
            fetchOrders();
            setIsDrawerOpen(false);
        } catch (err) {
            message.error(err.message || "Failed to send request");
        } finally {
            setUpdatingStatus(false);
        }
    };


    const formatScheduledTime = (timeStr, createdAt, status = '') => {
        if (!timeStr) return "";
        const slots = {
            '07:00 - 09:00': '07:00 AM - 09:00 AM',
            '10:00 - 12:00': '10:00 AM - 12:00 PM',
            '16:00 - 18:00': '04:00 PM - 06:00 PM'
        };
        if (slots[timeStr]) return slots[timeStr];
        
        const isTerminal = ['delivered', 'cancelled', 'returned', 'refund_completed', 'rejected', 'delivery_missed'].some(s => status?.toLowerCase().includes(s));
        const isActive = !isTerminal;

        const lower = timeStr.toLowerCase();
        if (lower === 'normal' && createdAt) {
            return `Express Delivery (ETA: ${dayjs(createdAt).add(120, 'minute').format('hh:mm A')})`;
        }
        if (lower === 'express' && createdAt) {
            return <DeliveryCountdown createdAt={createdAt} minutes={45} label="Express Delivery" isActive={isActive} />;
        }

        if (typeof timeStr === 'string' && timeStr.includes(':') && !timeStr.includes('-')) {
            try {
                return dayjs(`2024-01-01 ${timeStr}`).format("hh:mm A");
            } catch (e) { return timeStr; }
        }
        return timeStr;
    };

    const getStatusColor = (order) => {
        const s = getEffectiveStatus(order);
        if (s === 'order_updated') return 'orange';
        if (s === 'ordered') return 'blue';
        if (s === 'confirmed') return 'purple';
        if (s === 'assigned') return 'geekblue';
        if (s === 'packed') return 'orange';
        if (s === 'picked_up' || s === 'out_for_delivery' || s === 'shipped') return 'cyan';
        if (s === 'delivered') return 'green';
        if (s === 'cancelled' || s === 'delivery_missed' || s === 'no_delivery_available') return 'red';
        if (s === 'awaiting_buyer_approval') return 'gold';
        return 'default';
    };

    const getStatusLabel = (order) => {
        const s = getEffectiveStatus(order);
        if (!s) return 'UNKNOWN';
        if (s === 'order_updated') return 'Re-Ordered';
        return s.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
    };

    // 📋 Table Columns
    const columns = [
        {
            title: "Image",
            key: "image",
            width: 70,
            align: "center",
            render: (_, record) => (
                <TableImage
                    src={getImageUrl(record.items?.[0]?.imageUrl || record.items?.[0]?.product?.imageUrl)}
                    alt="order"
                />
            )
        },
        {
            title: "Order ID",
            dataIndex: "orderId",
            key: "orderId",
            width: 150,
            render: (text, record) => (
                <div
                    style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column' }}
                    onClick={(e) => {
                        e.stopPropagation();
                        setSelectedOrder(record);
                        setViewModalVisible(true);
                    }}
                >
                    <span style={{ color: record.status?.toLowerCase() === 'ordered' ? '#1b5e20' : '#1e293b', fontWeight: 600 }}>
                        {text}
                    </span>
                    <span style={{ fontSize: 11, color: record.status?.toLowerCase() === 'ordered' ? '#2e7d32' : '#94a3b8', marginTop: 2 }}>
                        {record.items?.length || 0} item{(record.items?.length || 0) !== 1 ? 's' : ''}
                    </span>
                </div>
            )
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status, record) => {
                const currentOrder = record;
                return (
                    <Tag color={getStatusColor(currentOrder)} className="pcu-padding-x-10 pcu-padding-y-4 pcu-font-12 pcu-font-600 pcu-radius-6">
                        {getStatusLabel(currentOrder).toUpperCase()}
                    </Tag>
                );
            }
        },
        {
            title: "Payment",
            dataIndex: "paymentStatus",
            key: "paymentStatus",
            render: (status, record) => {
                const isDelivered = record.status?.toLowerCase() === 'delivered';
                const isCOD = (record.paymentMethod || record.paymentType)?.toUpperCase() === 'COD';
                const actualStatus = (isDelivered && isCOD) ? 'paid' : (status?.toLowerCase() || 'pending');

                let color = 'orange';
                if (actualStatus === 'paid') color = 'green';
                else if (actualStatus === 'failed') color = 'red';
                else if (actualStatus === 'refunded') color = 'purple';
                return (
                    <Tag color={color} className="pcu-radius-6 pcu-font-11">
                        {actualStatus.toUpperCase()}
                    </Tag>
                );
            }
        },
        {
            title: "Method",
            key: "paymentMethod",
            render: (_, record) => {
                const { formatPaymentDisplay } = require('../../util/paymentFormatter');
                const method = formatPaymentDisplay(
                    record.paymentMethod || record.paymentType || 'COD',
                    record.paymentCollectionMethod || record.paymentMethod || record.paymentType,
                    record.paymentSubMethod
                );
                return (
                    <Tag color={method.startsWith('COD') && !method.includes('ONLINE') ? 'default' : 'cyan'} className="pcu-radius-6 pcu-font-11">
                        {method.toUpperCase()}
                    </Tag>
                );
            }
        },
        {
            title: "Delivery Slot",
            key: "scheduled",
            render: (_, record) => (
                record.scheduledDate ? (
                    <div>
                        <div className="pcu-font-13 pcu-font-500">{formatDate(record.scheduledDate)}</div>
                        <div className="pcu-font-11 pcu-color-orange">{formatScheduledTime(record.scheduledTime, record.createdAt, record.status)}</div>
                    </div>
                ) : (
                    <div>
                        <div className="pcu-font-13 pcu-font-500 pcu-color-body">Express Delivery</div>
                        <div className="pcu-font-11 pcu-color-muted">
                            ETA: {dayjs(record.createdAt).add(1, 'day').format('DD MMM, hh:mm A')}
                        </div>
                    </div>
                )
            )
        },
        {
            title: "Action",
            key: "action",
            align: "center",
            width: 100,
            render: (_, record) => (
                <Button
                    type="default"
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleView(record);
                    }}
                    style={{
                        borderRadius: 3,
                        fontWeight: 600,
                        background: '#ffffff',
                        color: '#1e293b',
                        border: '1px solid #e4e4e7',
                        boxShadow: '0 0 0 1px #e4e4e7',
                        height: 'auto',
                        padding: '0px 8px',
                        fontSize: 13,
                    }}
                >
                    View
                </Button>
            ),
        },
    ];


    return (
        <div className="ss-wrapper">
            <div className="ss-container">
                {/* Header Section */}
                <div className="ss-header">
                    <div className="ss-header-left">
                        <div className="ss-header-icon">
                            <AppstoreOutlined style={{ fontSize: 22, color: '#4776E6' }} />
                        </div>
                        <div>
                            <h1 className="ss-title">Manage Orders</h1>
                            <span style={{ color: "#7c8ba1", fontSize: "14px" }}>Manage Orders with Issue History</span>
                        </div>
                    </div>
                </div>

                <div className="ss-filter-bar" style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'flex-end' }}>
                    <div className="ss-filter-field" style={{ flex: '1 1 300px' }}>
                        <span className="ss-filter-label">Search Orders</span>
                        <Input
                            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                            placeholder="Search by Order ID or Customer Name"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            style={{ height: 40, borderRadius: 8 }}
                            allowClear
                        />
                    </div>

                    <div className="ss-filter-field" style={{ flex: '1 1 200px' }}>
                        <span className="ss-filter-label">Shop Name</span>
                        <Select
                            placeholder="All Shops"
                            style={{ width: '100%', height: 40 }}
                            value={selectedShop}
                            onChange={setSelectedShop}
                            allowClear
                        >
                            {allShops.map(shop => (
                                <Option key={shop._id} value={shop._id}>{shop.name}</Option>
                            ))}
                        </Select>
                    </div>

                    <div className="ss-filter-field" style={{ flex: '1 1 200px' }}>
                        <span className="ss-filter-label">Category</span>
                        <Select
                            placeholder="All Categories"
                            style={{ width: '100%', height: 40 }}
                            value={selectedCategory}
                            onChange={setSelectedCategory}
                            allowClear
                        >
                            {allCategories.map(cat => (
                                <Option key={cat._id} value={cat._id}>{cat.name}</Option>
                            ))}
                        </Select>
                    </div>

                    <div className="ss-filter-field" style={{ flex: '1 1 200px' }}>
                        <span className="ss-filter-label">Subcategory</span>
                        <Select
                            placeholder="All Subcategories"
                            style={{ width: '100%', height: 40 }}
                            value={selectedSubCategory}
                            onChange={setSelectedSubCategory}
                            allowClear
                            disabled={!selectedCategory}
                        >
                            {filteredSubCategories.map(sub => (
                                <Option key={sub._id} value={sub._id}>{sub.name}</Option>
                            ))}
                        </Select>
                    </div>

                    <div className="ss-filter-field" style={{ flex: '1 1 180px' }}>
                        <span className="ss-filter-label">Order Status</span>
                        <Select
                            placeholder="All Status"
                            style={{ width: '100%', height: 40 }}
                            value={selectedOrderStatus}
                            onChange={setSelectedOrderStatus}
                            allowClear
                        >
                            <Option value="ordered">Ordered</Option>
                            <Option value="awaiting_buyer_approval">Issue Pending</Option>
                            <Option value="confirmed">Confirmed</Option>
                            <Option value="assigned">Assigned</Option>
                            <Option value="packed">Packed</Option>
                            <Option value="picked_up">Picked Up</Option>
                            <Option value="delivered">Delivered</Option>
                            <Option value="cancelled">Cancelled</Option>
                        </Select>
                    </div>

                    <div className="ss-filter-reset" style={{ display: 'flex', alignItems: 'center', height: '40px' }}>
                        <button
                            className="ss-reset-btn"
                            style={{ marginTop: '0' }}
                            onClick={() => {
                                setSearchText("");
                                setSelectedShop(null);
                                setSelectedCategory(null);
                                setSelectedSubCategory(null);
                                setSelectedPaymentStatus(null);
                                setSelectedOrderStatus(null);
                                // Pass cleared values explicitly — state updates are async
                                fetchOrders("", { shopId: null, categoryId: null, subCategoryId: null, paymentStatus: null, status: null });
                            }}
                        >
                            <UndoOutlined /> Reset
                        </button>
                    </div>
                </div>

                {/* Table Section */}
                <div className="ss-table-wrap">
                    <Table
                        className="custom-table table-container"
                        columns={columns}
                        dataSource={orders}
                        rowKey={(record) => record._id}
                        rowClassName={(record) => getOrderRowClass(record)}
                        loading={loading}
                        pagination={{
                            className: "custom-pagination pcu-padding-16",
                            showTotal: (total) => `Total ${total} orders`,
                            showLessItems: true,
                            defaultPageSize: 10,
                            onChange: (page) => {
                                const pageContent = document.querySelector('.page-content');
                                if (pageContent) pageContent.scrollTo({ top: 0, behavior: 'smooth' });
                                else window.scrollTo({ top: 0, behavior: 'smooth' });
                            },
                        }}
                        bordered={false}
                        scroll={{ x: 'max-content' }}
                        locale={{
                            emptyText: (
                                <EmptyState
                                    title="No Orders Found"
                                    description={searchText
                                        ? "No orders match your search criteria. Try a different Order ID or Customer Name."
                                        : "You haven't received any orders yet. New orders will appear here automatically."
                                    }
                                    gradient="blue"
                                    showAction={false}
                                    icon="inbox"
                                />
                            )
                        }}
                    />
                </div>
            </div>

            {/* View Order Modal (Premium Compact Style) */}
            <Modal
                zIndex={1050}
                open={viewModalVisible}
                onCancel={() => setViewModalVisible(false)}
                footer={null}
                width={700}
                centered
                closable={false}
                bodyStyle={{ padding: 0 }}
                maskStyle={{ backdropFilter: 'blur(3px)' }}
                style={{ borderRadius: 20, overflow: 'hidden' }}
            >
                {selectedOrder && (
                    <div style={{ background: '#fff', borderRadius: 20, overflow: 'hidden' }}>
                        {/* Header Section - Compact Premium */}
                        <div style={{
                            padding: '16px 20px',
                            background: 'linear-gradient(135deg, #f0f7ff 0%, #f5f3ff 100%)',
                            borderBottom: '1px solid #e2e8f0',
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 14
                        }}>
                            <div style={{
                                width: 48, height: 48, background: '#fff', borderRadius: 12,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                boxShadow: '0 4px 10px rgba(0,0,0,0.04)', border: '1px solid #e2e8f0'
                            }}>
                                <AppstoreOutlined style={{ fontSize: 22, color: '#4f46e5' }} />
                            </div>
                            <div style={{ flex: 1 }}>
                                <h2 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: '#1e293b', lineHeight: 1.2 }}>
                                    Order {selectedOrder.orderId}
                                </h2>
                                <p style={{ margin: '1px 0 4px', fontSize: 12, color: '#64748b', fontWeight: 600 }}>
                                    Customer: <span style={{ color: '#1e293b' }}>{selectedOrder.user?.firstName} {selectedOrder.user?.lastName}</span>
                                </p>
                                <div style={{ display: 'flex', gap: 6 }}>
                                    <Tag color={getStatusColor(selectedOrder)} style={{ margin: 0, borderRadius: 5, fontWeight: 800, fontSize: 9, padding: '0 8px', border: 'none', background: '#fff', color: getStatusColor(selectedOrder) === 'default' ? '#64748b' : undefined, boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                                        {getStatusLabel(selectedOrder)}
                                    </Tag>
                                    <Tag color="blue" style={{ margin: 0, borderRadius: 5, fontWeight: 800, fontSize: 9, padding: '0 8px', border: 'none', background: '#fff', color: '#3b82f6', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                                        {selectedOrder.creditsUsed > 0 ? ((selectedOrder.codAmount === 0 && selectedOrder.payableAmount === 0) ? "CREDIT" : `CREDIT + ${(selectedOrder.paymentMethod?.toUpperCase() || selectedOrder.paymentType?.toUpperCase() || 'COD')}`) : (selectedOrder.paymentMethod?.toUpperCase() || selectedOrder.paymentType?.toUpperCase() || 'COD')}
                                    </Tag>
                                    {selectedOrder.paymentCollectionMethod && selectedOrder.paymentCollectionMethod !== selectedOrder.paymentMethod && (
                                        <Tag color="purple" style={{ margin: 0, borderRadius: 5, fontWeight: 800, fontSize: 9, padding: '0 8px', border: 'none', background: '#fff', color: '#8b5cf6', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                                            VIA {selectedOrder.paymentCollectionMethod?.toUpperCase()}
                                        </Tag>
                                    )}
                                    {selectedOrder.paymentSubMethod && (
                                        <Tag color="cyan" style={{ margin: 0, borderRadius: 5, fontWeight: 800, fontSize: 9, padding: '0 8px', border: 'none', background: '#fff', color: '#06b6d4', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                                            {selectedOrder.paymentSubMethod?.toUpperCase()}
                                        </Tag>
                                    )}
                                    {selectedOrder.transactionId && (
                                        <Tag color="default" style={{ margin: 0, borderRadius: 5, fontWeight: 800, fontSize: 9, padding: '0 8px', border: '1px solid #e2e8f0', background: '#f8fafc', color: '#64748b' }}>
                                            TXN: {selectedOrder.transactionId}
                                        </Tag>
                                    )}
                                    {selectedOrder.productIssueRequest?.status === 'ACCEPTED' && (
                                        <Tag style={{ margin: 0, borderRadius: 5, fontWeight: 800, fontSize: 9, padding: '0 8px', border: '1px solid #b7eb8f', background: '#f6ffed', color: '#389e0d', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                                            Order Updated (Accepted)
                                        </Tag>
                                    )}
                                </div>
                            </div>
                            <button
                                onClick={() => setViewModalVisible(false)}
                                style={{
                                    position: 'absolute', top: 16, right: 16, border: 'none', background: '#fff',
                                    width: 28, height: 28, borderRadius: '50%', cursor: 'pointer', color: '#94a3b8',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(0,0,0,0.05)'
                                }}
                            >
                                <EyeOutlined style={{ fontSize: 14 }} />
                            </button>
                        </div>

                        {selectedOrder.productIssueHistory && selectedOrder.productIssueHistory.length > 0 && (
                            <div style={{ padding: '12px 20px', background: '#fffbeb', borderBottom: '1px solid #fef3c7', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#d97706' }}></div>
                                    <span style={{ fontSize: 13, fontWeight: 700, color: '#92400e' }}>
                                        {selectedOrder.productIssueHistory.length} Issue Request{selectedOrder.productIssueHistory.length !== 1 ? 's' : ''} Found
                                    </span>
                                </div>
                                <Button 
                                    size="small" 
                                    style={{ background: '#f59e0b', color: '#fff', border: 'none', fontWeight: 600, fontSize: 12, borderRadius: 6 }}
                                    onClick={() => setHistoryModalVisible(true)}
                                >
                                    View History
                                </Button>
                            </div>
                        )}

                        <div style={{ padding: '16px 20px', maxHeight: '65vh', overflowY: 'auto' }}>
                            {/* Info Grid - Compact */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 16 }}>
                                <div style={{ background: '#f8fafc', padding: '8px 12px', borderRadius: 10, border: '1px solid #f1f5f9' }}>
                                    <div style={{ fontSize: 8, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 1 }}>Email Address</div>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis' }}>{selectedOrder.user?.email || 'N/A'}</div>
                                </div>
                                <div style={{ background: '#f8fafc', padding: '8px 12px', borderRadius: 10, border: '1px solid #f1f5f9' }}>
                                    <div style={{ fontSize: 8, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 1 }}>Phone Number</div>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>{selectedOrder.user?.phone || 'N/A'}</div>
                                </div>
                                <div style={{ background: '#f8fafc', padding: '8px 12px', borderRadius: 10, border: '1px solid #f1f5f9' }}>
                                    <div style={{ fontSize: 8, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 1 }}>Ordered On</div>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>{formatDateTime(selectedOrder.createdAt)}</div>
                                </div>

                                <div style={{ gridColumn: 'span 3', background: selectedOrder.status === 'delivered' ? '#f0fdf4' : '#fff7ed', padding: '8px 12px', borderRadius: 10, border: `1px solid ${selectedOrder.status === 'delivered' ? '#bbf7d0' : '#ffedd5'}` }}>
                                    <div style={{ fontSize: 8, fontWeight: 800, color: selectedOrder.status === 'delivered' ? '#166534' : '#c2410c', textTransform: 'uppercase', marginBottom: 1 }}>
                                        {selectedOrder.status === 'delivered' ? 'Delivered At' : 'Delivery Slot'}
                                    </div>
                                    <div style={{ fontSize: 13, fontWeight: 700, color: selectedOrder.status === 'delivered' ? '#15803d' : '#9a3412' }}>
                                        {selectedOrder.status === 'delivered' ? (
                                            `${dayjs(selectedOrder.deliveredAt || selectedOrder.updatedAt).format("DD MMM YYYY")} at ${dayjs(selectedOrder.deliveredAt || selectedOrder.updatedAt).format("hh:mm A")}`
                                        ) : selectedOrder.scheduledDate ? (
                                            `${formatDate(selectedOrder.scheduledDate)} at ${formatScheduledTime(selectedOrder.scheduledTime, selectedOrder.createdAt, selectedOrder.status)}`
                                        ) : (
                                            <DeliveryCountdown createdAt={selectedOrder.createdAt} minutes={45} label="Express Delivery" isActive={!['delivered', 'cancelled', 'returned'].some(s => selectedOrder.status?.toLowerCase().includes(s))} />
                                        )}
                                    </div>
                                </div>

                                <div style={{ gridColumn: 'span 3', background: '#f8fafc', padding: '8px 12px', borderRadius: 10, border: '1px solid #f1f5f9' }}>
                                    <div style={{ fontSize: 8, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 1 }}>Delivery Address</div>
                                    <div style={{ fontSize: 12, fontWeight: 500, color: '#475569', lineHeight: 1.4 }}>
                                        {selectedOrder.deliveryAddress?.street}, {selectedOrder.deliveryAddress?.city}, {selectedOrder.deliveryAddress?.district}, {selectedOrder.deliveryAddress?.pincode}
                                    </div>
                                </div>
                            </div>

                            {/* Order Items Section - Compact */}
                            <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 10, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div style={{ width: 3, height: 14, background: '#4f46e5', borderRadius: 2 }}></div>
                                Order Items
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {selectedOrder.items?.map((item, idx) => (
                                    <div key={idx} style={{
                                        display: 'flex', gap: 14, padding: '12px 0',
                                        borderBottom: '1px solid #f1f5f9', alignItems: 'flex-start'
                                    }}>
                                        <div style={{ width: 56, height: 56, borderRadius: 10, overflow: 'hidden', border: '1px solid #f1f5f9', flexShrink: 0 }}>
                                            <img src={getImageUrl(item.imageUrl || item.product?.imageUrl)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="" />
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontWeight: 800, fontSize: 14, color: '#1e293b', marginBottom: 2 }}>{item.name}</div>
                                            <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>
                                                Weight: {(() => {
                                                    let opts = item.selectedOptions || item.options;
                                                    if (typeof opts === 'string' && opts.startsWith('{')) {
                                                        try { opts = JSON.parse(opts); } catch (e) { }
                                                    }
                                                    if (opts && typeof opts === 'object' && !Array.isArray(opts) && Object.keys(opts).length > 0) {
                                                        return Object.entries(opts).map(([k, v]) => formatUnit(v, k)).join(', ');
                                                    }
                                                    return item.variantName || item.unit || item.weight || item.product?.unit || item.product?.weight || '1 Unit';
                                                })()}
                                            </div>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'flex-end', gap: 8 }}>
                                                <span style={{ fontSize: 11, color: '#94a3b8', textDecoration: 'line-through', fontWeight: 500 }}>
                                                    ₹{(item.mrp || item.price).toFixed(2)}
                                                </span>
                                                <span style={{ fontWeight: 900, fontSize: 15, color: '#1e293b' }}>
                                                    ₹{(item.price * item.quantity).toFixed(2)}
                                                </span>
                                            </div>
                                            <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 700 }}>Qty: {item.quantity}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Summary Card - Compact */}
                            <div style={{ marginTop: 16, background: '#f8fafc', borderRadius: 14, padding: '12px 16px', border: '1px solid #f1f5f9' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontSize: 13, color: '#1e293b', fontWeight: 800 }}>Item Total</span>
                                    <span style={{ fontSize: 18, color: '#4f46e5', fontWeight: 900 }}>{formatPrice(selectedOrder.totalAmount - (selectedOrder.deliveryCharge || 0))}</span>
                                </div>
                            </div>
                        </div>

                        {/* Footer Section - Compact */}
                        <div style={{ padding: '12px 20px 16px', display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #f1f5f9' }}>
                            <div style={{ display: 'flex', gap: 10 }}>
                                {(selectedOrder.status === 'picked_up' || selectedOrder.status === 'out_for_delivery') && (
                                    <button
                                        style={{
                                            background: '#fff', border: '1px solid #e2e8f0', color: '#f59e0b', fontSize: 12, fontWeight: 700,
                                            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8
                                        }}
                                        onClick={() => setRequestModalVisible(true)}
                                    >
                                        <ReloadOutlined /> Request Time Change
                                    </button>
                                )}
                            </div>
                            <button
                                onClick={() => setViewModalVisible(false)}
                                style={{
                                    padding: '6px 24px', borderRadius: 8, border: '1px solid #e2e8f0',
                                    background: '#f8fafc', color: '#64748b', fontSize: 12, fontWeight: 700,
                                    cursor: 'pointer', transition: 'all 0.2s'
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
                                onMouseLeave={e => e.currentTarget.style.background = '#f8fafc'}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                )}
            </Modal>

            <OrderStatusDrawer
                open={isDrawerOpen}
                onClose={() => setIsDrawerOpen(false)}
                orders={selectedOrder ? [selectedOrder] : []}
                isSeller={true}
                onStatusUpdate={handleStatusUpdate}
                onViewDetails={(order) => {
                    setSelectedOrder(order);
                    setViewModalVisible(true);
                }}
                onReschedule={(order) => { setSelectedOrder(order); setRescheduleModalVisible(true); }}
                onRequestUpdate={(order) => { setSelectedOrder(order); setRequestUpdateModalVisible(true); }}
            />

            {/* Request Delivery Time Change Modal */}
            <Modal
                title="Request Delivery Time Change"
                open={requestModalVisible}
                onCancel={() => setRequestModalVisible(false)}
                onOk={handleRequestTimeChange}
                confirmLoading={updatingStatus}
                okText="Send Request to Buyer"
            >
                <div className="pcu-padding-y-10">
                    <p>Select a new date and time for delivery. The buyer must approve this change.</p>
                    <div className="pcu-margin-bottom-16">
                        <Text strong className="pcu-block pcu-margin-bottom-6">New Date</Text>
                        <Input
                            type="date"
                            className="pcu-w-100"
                            value={requestDate ? dayjs(requestDate).format('YYYY-MM-DD') : ''}
                            onChange={(e) => setRequestDate(dayjs(e.target.value))}
                        />
                    </div>
                    <div>
                        <Text strong className="pcu-block pcu-margin-bottom-6">New Time Slot</Text>
                        <Select
                            className="pcu-w-100"
                            placeholder="Select Time Slot"
                            onChange={(val) => setRequestTime(val)}
                        >
                            <Option value="07:00 - 09:00">Morning (07:00 AM - 09:00 AM)</Option>
                            <Option value="10:00 - 12:00">Noon (10:00 AM - 12:00 PM)</Option>
                            <Option value="16:00 - 18:00">Evening (04:00 PM - 06:00 PM)</Option>
                        </Select>
                    </div>
                </div>
            </Modal>

            {/* Reschedule Order Modal */}
            <Modal
                title="Reschedule Order"
                open={rescheduleModalVisible}
                onCancel={() => setRescheduleModalVisible(false)}
                onOk={handleSellerReschedule}
                confirmLoading={updatingStatus}
                okText="Reschedule"
            >
                <div className="pcu-padding-y-10">
                    <div className="pcu-margin-bottom-16">
                        <Text strong className="pcu-block pcu-margin-bottom-6">New Date</Text>
                        <Input
                            type="date"
                            className="pcu-w-100"
                            value={requestDate ? dayjs(requestDate).format('YYYY-MM-DD') : ''}
                            onChange={(e) => setRequestDate(dayjs(e.target.value))}
                        />
                    </div>
                    <div className="pcu-margin-bottom-16">
                        <Text strong className="pcu-block pcu-margin-bottom-6">New Time Slot</Text>
                        <Select
                            className="pcu-w-100"
                            placeholder="Select Time Slot"
                            onChange={(val) => setRequestTime(val)}
                        >
                            <Option value="07:00 - 09:00">Morning (07:00 AM - 09:00 AM)</Option>
                            <Option value="10:00 - 12:00">Noon (10:00 AM - 12:00 PM)</Option>
                            <Option value="16:00 - 18:00">Evening (04:00 PM - 06:00 PM)</Option>
                        </Select>
                    </div>
                    <div>
                        <Text strong className="pcu-block pcu-margin-bottom-6">Reason (Optional)</Text>
                        <Input.TextArea
                            rows={3}
                            placeholder="Why are you rescheduling?"
                            value={rescheduleReason}
                            onChange={(e) => setRescheduleReason(e.target.value)}
                        />
                    </div>
                </div>
            </Modal>

            {/* Unified Order Issue Request Modal - Premium Style */}
            <Modal
                title={<span style={{ fontSize: 18, fontWeight: 800, color: "#1e293b" }}>Send Product Issue Request to Buyer</span>}
                open={requestUpdateModalVisible}
                onCancel={() => {
                    setRequestUpdateModalVisible(false);
                    setSelectedProducts([]);
                    setIssueActionType(null);
                    setCancellationReason("");
                }}
                onOk={handleSellerRequestUpdate}
                confirmLoading={updatingStatus}
                okText="Send Request"
                width={650}
                centered
                className="custom-modal"
                bodyStyle={{ padding: '20px 24px 24px', background: '#f8fafc' }}
                style={{ borderRadius: 16, overflow: "hidden" }}
                maskStyle={{ backdropFilter: "blur(4px)" }}
            >
                <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                    {/* Step 1: Product Selection */}
                    <div style={{
                        background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.02)", padding: 16
                    }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                            <div style={{ fontSize: 10, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                1. Select Products
                            </div>
                            <Button 
                                type="link" 
                                size="small" 
                                style={{ fontSize: 12, fontWeight: 600, padding: 0, height: "auto" }}
                                onClick={() => {
                                    if (selectedProducts.length === selectedOrder?.items?.length) {
                                        setSelectedProducts([]);
                                    } else {
                                        setSelectedProducts(selectedOrder?.items?.map(i => i.product?._id || i.product) || []);
                                    }
                                }}
                            >
                                {selectedProducts.length === selectedOrder?.items?.length ? "Deselect All" : "Select All"}
                            </Button>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 220, overflowY: "auto", paddingRight: 4 }}>
                            {selectedOrder?.items?.map((item, index) => {
                                const productId = item.product?._id || item.product;
                                const isSelected = selectedProducts.includes(productId);
                                return (
                                    <div key={index} style={{
                                        display: "flex", alignItems: "center", gap: 12, padding: "10px 14px",
                                        borderRadius: 10, border: `1px solid ${isSelected ? '#4f46e5' : '#f1f5f9'}`,
                                        background: isSelected ? '#eef2ff' : '#fff', transition: "all 0.2s ease"
                                    }}>
                                        <input
                                            type="checkbox"
                                            checked={isSelected || issueActionType === 'CANCEL_FULL_ORDER'}
                                            disabled={issueActionType === 'CANCEL_FULL_ORDER'}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSelectedProducts([...selectedProducts, productId]);
                                                } else {
                                                    setSelectedProducts(selectedProducts.filter(id => id !== productId));
                                                }
                                            }}
                                            style={{ width: 16, height: 16, cursor: 'pointer', accentColor: "#4f46e5" }}
                                        />
                                        <div style={{ width: 40, height: 40, borderRadius: 8, overflow: 'hidden', border: '1px solid #e2e8f0', flexShrink: 0 }}>
                                            <img 
                                                src={getImageUrl(item.product?.imageUrl || item.product?.images?.[0] || item.imageUrl) || "https://via.placeholder.com/40?text=NA"} 
                                                alt={item.name} 
                                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                                onError={(e) => e.target.src = "https://via.placeholder.com/40?text=NA"}
                                            />
                                        </div>
                                        <div style={{ display: "flex", flexDirection: "column" }}>
                                            <span style={{ fontWeight: 700, fontSize: 13, color: isSelected ? "#312e81" : "#1e293b" }}>{item.name}</span>
                                            <span style={{ fontSize: 11, fontWeight: 600, color: isSelected ? "#4f46e5" : "#64748b" }}>Qty: {item.quantity} | {item.unit || item.weight || '1 unit'}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Step 2: Issue Action Type */}
                    <div style={{
                        background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.02)", padding: 16
                    }}>
                        <div style={{ fontSize: 10, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 12 }}>
                            2. Select Issue Action Type
                        </div>
                        <Select
                            style={{ width: "100%", height: 42 }}
                            placeholder="Select the action you want to take"
                            value={issueActionType}
                            onChange={(val) => {
                                setIssueActionType(val);
                                if (val !== 'OUT_OF_STOCK') {
                                    setAvailabilityOption(null);
                                }
                                if (val === 'CANCEL_FULL_ORDER') {
                                    setSelectedProducts(selectedOrder?.items?.map(i => i.product?._id || i.product) || []);
                                }
                            }}
                            className="custom-select"
                        >
                            <Option value="OUT_OF_STOCK"><span style={{ fontWeight: 600, color: "#334155" }}>Out of Stock</span></Option>
                            <Option value="CANCEL_FULL_ORDER"><span style={{ fontWeight: 700, color: "#ef4444" }}>Cancel Full Order</span></Option>
                            <Option value="CUSTOM_REASON"><span style={{ fontWeight: 600, color: "#334155" }}>Custom</span></Option>
                        </Select>

                        {issueActionType === 'OUT_OF_STOCK' && (
                            <div style={{ marginTop: 16, animation: "fadeIn 0.3s ease-in-out" }}>
                                <div style={{ fontSize: 10, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>
                                    Product availability status <span style={{ color: "#ef4444" }}>*</span>
                                </div>
                                <Select
                                    style={{ width: "100%", height: 42 }}
                                    placeholder="Select availability status"
                                    value={availabilityOption}
                                    onChange={(val) => setAvailabilityOption(val)}
                                    className="custom-select"
                                >
                                    <Option value="Out of stock"><span style={{ fontWeight: 600, color: "#334155" }}>Out of stock</span></Option>
                                    <Option value="Available in 4 Hours"><span style={{ fontWeight: 600, color: "#334155" }}>Available in 4 Hours</span></Option>
                                    <Option value="Available in 6 Hours"><span style={{ fontWeight: 600, color: "#334155" }}>Available in 6 Hours</span></Option>
                                    <Option value="Available Tomorrow"><span style={{ fontWeight: 600, color: "#334155" }}>Available Tomorrow</span></Option>
                                </Select>
                            </div>
                        )}

                        {/* Reason Input Field */}
                        {(issueActionType === 'CANCEL_SELECTED_PRODUCT' || issueActionType === 'CANCEL_FULL_ORDER' || issueActionType === 'CUSTOM_REASON') && (
                            <div style={{ marginTop: 16, animation: "fadeIn 0.3s ease-in-out" }}>
                                <div style={{ fontSize: 10, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>
                                    Cancellation / Custom Reason <span style={{ color: "#ef4444" }}>*</span>
                                </div>
                                <Input.TextArea
                                    rows={3}
                                    placeholder="Enter reason (e.g. Product out of stock, Shop closed, Quality issue)"
                                    value={cancellationReason}
                                    onChange={(e) => setCancellationReason(e.target.value)}
                                    style={{ borderRadius: 8, borderColor: "#e2e8f0", padding: 12, fontSize: 13 }}
                                />
                                {(issueActionType === 'CANCEL_FULL_ORDER') && (
                                    <div style={{
                                        marginTop: 12, padding: "10px 12px", background: "#fef2f2",
                                        borderRadius: 8, border: "1px solid #fecaca", display: "flex", gap: 8, alignItems: "flex-start"
                                    }}>
                                        <IconAlertCircle size={16} color="#ef4444" style={{ marginTop: 1 }} />
                                        <span style={{ fontSize: 12, color: "#b91c1c", fontWeight: 600, lineHeight: 1.4 }}>
                                            Warning: You are requesting to cancel the full order. This will pause the order flow and mark it as "Issue Pending Buyer Response" for buyer action.
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </Modal>

            {/* Issue History Modal */}
            <Modal
                title={<span style={{ fontSize: 18, fontWeight: 800, color: "#1e293b" }}>Product Issue History</span>}
                open={historyModalVisible}
                onCancel={() => setHistoryModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setHistoryModalVisible(false)} style={{ borderRadius: 8, fontWeight: 600 }}>
                        Close
                    </Button>
                ]}
                width={650}
                centered
                bodyStyle={{ padding: '20px 24px', maxHeight: '60vh', overflowY: 'auto', background: '#f8fafc' }}
                style={{ borderRadius: 16, overflow: "hidden" }}
                maskStyle={{ backdropFilter: "blur(4px)" }}
            >
                {selectedOrder?.productIssueHistory?.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8' }}>No issue history found.</div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {selectedOrder?.productIssueHistory?.map((item, index) => {
                            let statusColor = '#f59e0b';
                            let statusBg = '#fef3c7';
                            if (item.status === 'ACCEPTED') { statusColor = '#10b981'; statusBg = '#d1fae5'; }
                            if (item.status === 'REJECTED') { statusColor = '#ef4444'; statusBg = '#fee2e2'; }

                            return (
                                <div key={index} style={{
                                    background: '#fff', padding: 16, borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                        <span style={{ fontWeight: 800, fontSize: 14, color: '#1e293b' }}>
                                            Issue: {item.issueType.replace(/_/g, ' ')}
                                        </span>
                                        <Tag style={{ margin: 0, borderRadius: 6, fontWeight: 800, fontSize: 10, padding: '2px 8px', border: 'none', background: statusBg, color: statusColor }}>
                                            {item.status}
                                        </Tag>
                                    </div>
                                    
                                    {item.cancellationReason && (
                                        <div style={{ fontSize: 13, color: '#475569', marginBottom: 4 }}>
                                            <span style={{ fontWeight: 700 }}>Reason:</span> {item.cancellationReason}
                                        </div>
                                    )}
                                    {item.availabilityReason && (
                                        <div style={{ fontSize: 13, color: '#475569', marginBottom: 4 }}>
                                            <span style={{ fontWeight: 700 }}>Availability:</span> {item.availabilityReason}
                                        </div>
                                    )}

                                    <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>
                                        <span>Requested: {item.requestedAt ? new Date(item.requestedAt).toLocaleString() : 'N/A'}</span>
                                        {item.respondedAt && <span>Responded: {new Date(item.respondedAt).toLocaleString()}</span>}
                                    </div>

                                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
                                        <span style={{ fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>Affected Items</span>
                                        <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                            {item.items?.map((pItem, pIdx) => (
                                                <div key={pIdx} style={{ fontSize: 12, color: '#334155', fontWeight: 500 }}>
                                                    • {pItem.product?.name || 'Product'} ({pItem.type})
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default OrderIssues;
